import { useState, useContext, useEffect } from 'react';
import gql from 'graphql-tag';
import _ from 'lodash';
import MercuryContext from 'ConnectionManager/mercuryContext';
import AccountHelper from 'Workspace/helpers/AccountHelper';

const queryLearnerProgress = gql`
query getProgressReport(
  $cohortId: UUID!,
  $deploymentId: UUID!,
  $activityId: UUID!,
  $isWorkspace: Boolean!
) {
  cohortById(cohortId: $cohortId) {
    id
    deployment(deploymentId: $deploymentId, isWorkspace: $isWorkspace) {
      activity(activityId: $activityId) {
        id
        elementType
        coursewareWalkableConfigurationFields(fieldNames:["title"]) {
          fieldName,
          fieldValue
        }
        pathways {
          walkables(cohortId: $cohortId) {
            edges {
              node {
                id
                elementType
                coursewareWalkableConfigurationFields(fieldNames:["title"]) {
                  fieldName,
                  fieldValue
                }
              }
            }
          }
        }
      }
    }
    enrollments {
      edges {
        node {
          account {
            id
            identity {
              email
              familyName
              givenName
              honorificSuffix
              avatars(size:SMALL) {
                data
              }
            }
          }
          learnerPerformanceByDeployment(deploymentId: $deploymentId, activityId: $activityId) {
            progress {
              id
              completion {
                value
              }
            }
          }
        }
      }
    }
  }
}
`;

export default function useLearnerProgress(cohortId, deploymentId, activityId) {
  const { apolloClient } = useContext(MercuryContext);
  const [state, setState] = useState({ data: {} });

  useEffect(() => {
    if (!activityId || !apolloClient) {
      return;
    }

    setState({ loading: true, error: null, data: {} });
    apolloClient.query({
      fetchPolicy: 'cache-first',
      query: queryLearnerProgress,
      variables: {
        cohortId,
        deploymentId,
        activityId,
        isWorkspace: true,
      },
    })
      .then((response) => {
        setState({
          loading: false,
          error: null,
          data: {
            enrollments: _.chain(response)
              .get('data.cohortById.enrollments.edges')
              .map((edge) => edge.node)
              .map(({ account, ...rest }) => ({
                account,
                progress: _.get(rest, 'learnerPerformanceByDeployment.progress.completion.value'),
              }))
              .sort((a, b) => AccountHelper.sortListByFamilyName(a.account.identity, b.account.identity))
              .value(),
            activity: _.chain(response)
              .get('data.cohortById.deployment.0.activity')
              .thru(({ id, elementType, ...rest }) => ({
                id,
                elementType,
                title: _.get(rest, 'coursewareWalkableConfigurationFields.0.fieldValue'),
              }))
              .value(),
            childActivities: _.chain(response)
              .get('data.cohortById.deployment.0.activity.pathways.0.walkables.edges')
              .map((edge) => edge.node)
              .map(({ id, elementType, ...rest }) => ({
                id,
                elementType,
                title: _.get(rest, 'coursewareWalkableConfigurationFields.0.fieldValue'),
              }))
              .filter(({ elementType }) => elementType === 'ACTIVITY')
              .value(),
          },
        });
      })
      .catch((error) => setState({ loading: false, error, data: {} }));
  }, [apolloClient, cohortId, deploymentId, activityId]);

  return state;
}
